// src/components/dashboard/widgets/google-listing-picker.tsx
import { useState } from 'react';
import { Search, Check, Loader2 } from 'lucide-react';
import { supabase } from '@/src/lib/supabase';
import { useAppColors } from '@/src/lib/theme';

interface Candidate { placeId: string; name: string; address: string }

interface GoogleListingPickerProps {
  venueId: string;
  defaultQuery: string;
  onConfirmed: (result: { rating: number | null; reviewCount: number | null }) => void;
}

export default function GoogleListingPicker({ venueId, defaultQuery, onConfirmed }: GoogleListingPickerProps) {
  const c = useAppColors();
  const [query, setQuery] = useState(defaultQuery);
  const [searching, setSearching] = useState(false);
  const [candidates, setCandidates] = useState<Candidate[] | null>(null);
  const [confirming, setConfirming] = useState<string | null>(null);
  const [confirmed, setConfirmed] = useState(false);

  async function search() {
    if (!query.trim()) return;
    setSearching(true);
    const { data } = await supabase.functions.invoke('venue-reviews', { body: { action: 'search', venue_id: venueId, query: query.trim() } });
    setSearching(false);
    setCandidates(data?.ok ? (data.candidates as Candidate[]) : []);
  }

  async function confirm(placeId: string) {
    setConfirming(placeId);
    const { data } = await supabase.functions.invoke('venue-reviews', { body: { action: 'confirm', venue_id: venueId, place_id: placeId } });
    setConfirming(null);
    if (data?.ok) {
      setConfirmed(true);
      onConfirmed({ rating: data.rating ?? null, reviewCount: data.reviewCount ?? null });
    }
  }

  if (confirmed) {
    return (
      <div className="flex items-center gap-2 text-xs" style={{ color: c.accentSoft }}>
        <Check size={14} /> Google Business listing confirmed
      </div>
    );
  }

  return (
    <div className="space-y-2">
      <label className="text-[10px] font-bold uppercase tracking-wider block" style={{ color: c.textFaint }}>
        Confirm your Google Business listing
      </label>
      <div className="flex gap-2">
        <input
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); search(); } }}
          placeholder="Search your business name"
          className="flex-1 h-10 rounded-lg px-3 text-sm outline-none"
          style={{ background: c.iconBg, border: `1px solid ${c.border}`, color: c.text }}
        />
        <button
          type="button"
          onClick={search}
          disabled={searching}
          className="h-10 px-3 rounded-lg flex items-center justify-center disabled:opacity-50"
          style={{ background: c.accentBg, border: `1px solid ${c.accentBorder}` }}
        >
          {searching ? <Loader2 size={14} className="animate-spin" /> : <Search size={14} style={{ color: c.accentSoft }} />}
        </button>
      </div>

      {candidates && candidates.length === 0 && (
        <p className="text-xs" style={{ color: c.textFaint }}>No matches found — try a different search.</p>
      )}

      {candidates && candidates.length > 0 && (
        <div className="rounded-lg overflow-hidden" style={{ border: `1px solid ${c.border}` }}>
          {candidates.map((cand) => (
            <button
              key={cand.placeId}
              type="button"
              onClick={() => confirm(cand.placeId)}
              disabled={confirming !== null}
              className="w-full text-left px-3 py-2.5 text-sm border-b last:border-b-0 flex items-center justify-between gap-2 disabled:opacity-50"
              style={{ borderColor: c.border, color: c.text, background: c.iconBg }}
            >
              <span className="min-w-0">
                <div className="truncate">{cand.name}</div>
                <div className="text-[11px] truncate" style={{ color: c.textFaint }}>{cand.address}</div>
              </span>
              {confirming === cand.placeId ? <Loader2 size={14} className="animate-spin flex-shrink-0" /> : <Check size={14} className="flex-shrink-0" style={{ color: c.textFaint }} />}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}